/** Wersja angielska — typowana względem polskiej (brak klucza = błąd kompilacji). */
import { fleetReportsPl, type Shape } from './fleet-reports-pl';
import { fleetReportsEnOps } from './fleet-reports-en-ops';

export const fleetReportsEn: Shape<typeof fleetReportsPl> = {
  nav: { fuel: 'Fuel', expenses: 'Expenses', incidents: 'Incidents', checklists: 'Checklists', service: 'Service', templates: 'Templates', dashboard: 'Fleet dashboard', analytics: 'Analytics', routes: 'Routes', integration: 'Route integration' },
  enums: {
    reviewStatus: { SUBMITTED: 'Awaiting review', APPROVED: 'Approved', REJECTED: 'Rejected', NEEDS_INFO: 'Needs info' },
    reviewEvent: { create: 'Entry added', approve: 'Approved', reject: 'Rejected', 'request-info': 'Asked for more info', resubmit: 'Completed and resubmitted' },
    reviewAction: { approve: 'Approve', reject: 'Reject', 'request-info': 'Request more info' },
    fuelType: { DIESEL: 'Diesel', PETROL: 'Petrol', LPG: 'LPG', CNG: 'CNG', ELECTRIC: 'Electric', ADBLUE: 'AdBlue', OTHER: 'Other' },
    paymentMethod: { FUEL_CARD: 'Fuel card', COMPANY_CARD: 'Company card', CASH: 'Cash', PRIVATE: 'Private funds', OTHER: 'Other' },
    fuelSource: { MANUAL: 'Driver entry', CARD_IMPORT: 'Card import' },
    fuelWarning: {
      ODOMETER_BELOW_PREVIOUS: 'Odometer below previous',
      ODOMETER_MISSING: 'Odometer missing',
      AMOUNT_MISMATCH: 'Amount ≠ litres × price',
      UNUSUAL_PRICE: 'Unusual price',
      LITERS_ABOVE_CAPACITY: 'More than tank capacity',
      DUPLICATE_SUSPECTED: 'Possible duplicate',
    },
    fuelWarningHelp: {
      ODOMETER_BELOW_PREVIOUS: 'The odometer value is lower than the previous reading of this vehicle. No reading was saved and the vehicle odometer did not change — check the receipt and ask for corrections or fix the data.',
      ODOMETER_MISSING: 'The entry has no odometer value, so no reading was created and consumption cannot be calculated.',
      AMOUNT_MISMATCH: 'The total does not match litres × unit price (beyond a 1% or 0.05 tolerance). Compare with the receipt photo.',
      UNUSUAL_PRICE: 'The unit price deviates by more than 40% from the median of recent fills of this fuel (or is outside a sensible range).',
      LITERS_ABOVE_CAPACITY: 'More litres were filled than the vehicle tank capacity. It may be a data error or a fill into a jerry can.',
      DUPLICATE_SUSPECTED: 'Another fill of this vehicle with the same litres was saved within 2 hours — check it is not the same fill entered twice.',
    },
    expenseCategory: { PARKING: 'Parking', TOLL: 'Tolls', CAR_WASH: 'Car wash', MINOR_REPAIR: 'Minor repair', FLUIDS: 'Fluids', OTHER: 'Other' },
    incidentType: { BREAKDOWN: 'Breakdown', DAMAGE: 'Damage', COLLISION: 'Collision', IRREGULARITY: 'Irregularity', OTHER: 'Other' },
    severity: { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', CRITICAL: 'Critical' },
    incidentStatus: { NEW: 'New', IN_PROGRESS: 'In progress', RESOLVED: 'Resolved', CLOSED: 'Closed' },
    incidentStatusAction: { NEW: 'Mark as new', IN_PROGRESS: 'Take on (in progress)', RESOLVED: 'Mark as resolved', CLOSED: 'Close incident' },
    decision: { NONE: 'No decision', PENDING: 'Awaiting decision', ALLOWED: 'Cleared to drive', BLOCKED: 'Blocked' },
    incidentEvent: {
      CREATED: 'Reported',
      STATUS_CHANGED: 'Status changed',
      COMMENT: 'Comment',
      DECISION: 'Vehicle decision',
      VEHICLE_BLOCKED: 'Vehicle blocked automatically',
      UPDATED: 'Incident data changed',
    },
    checklistPhase: { BEFORE_TRIP: 'Before trip', AFTER_TRIP: 'After trip' },
    checklistResult: { OK: 'OK', WARNING: 'Warning', DEFECT: 'Defect' },
    requirement: { NEVER: 'Not required', ON_ISSUE: 'On warning or defect', ALWAYS: 'Always' },
    serviceType: { INSPECTION: 'Inspection', SERVICE: 'Service', REPAIR: 'Repair', TIRES: 'Tyres', DEFECT: 'Defect', OTHER: 'Other' },
    serviceStatus: { PLANNED: 'Planned', IN_PROGRESS: 'In progress', DONE: 'Done' },
    fieldType: { text: 'Text', number: 'Number', boolean: 'Yes / no', select: 'Choice list', date: 'Date' },
    defectProcedure: {
      NOTIFY_ONLY: 'Notification only',
      BLOCK_VEHICLE: 'Block the vehicle automatically',
      REQUIRE_DECISION: 'Require a decision from an authorised person',
    },
    defectProcedureHelp: {
      NOTIFY_ONLY: 'People handling incidents get a notification; the vehicle can keep driving.',
      BLOCK_VEHICLE: 'The vehicle immediately goes to “In service”; releasing it after repair is a vehicle status change.',
      REQUIRE_DECISION: 'The vehicle cannot start a new logbook until someone with permission decides: clear it to drive or block it.',
    },
  },
  common: {
    all: 'All',
    allVehicles: 'All vehicles',
    allDrivers: 'All drivers',
    allStatuses: 'All statuses',
    filterVehicle: 'Vehicle',
    filterDriver: 'Driver',
    filterStatus: 'Status',
    filterPeriod: 'Period (month)',
    comment: 'Comment',
    optional: 'optional',
    history: 'History',
    details: 'Details',
    noHistory: 'No history entries.',
    back: 'Back to the list',
    openVehicle: 'Vehicle page',
    openLogbook: 'Logbook',
    driver: 'Driver',
    vehicle: 'Vehicle',
    date: 'Date',
    status: 'Status',
    amount: 'Amount',
    reviewedBy: 'Reviewed by: {name}',
    notFoundHint: 'The entry does not exist or you have no access to it.',
    saved: 'Saved.',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    yes: 'Yes',
    no: 'No',
    loadMore: 'Show all',
    viewAll: 'View all',
    none: 'None',
    unknown: 'unknown',
  },
  attach: {
    title: 'Attachments',
    upload: 'Add file',
    uploaded: 'Attachment added.',
    deleted: 'Attachment removed.',
    empty: 'No attachments.',
    deleteConfirm: 'Remove attachment “{name}”?',
    download: 'Download {name}',
    delete: 'Remove {name}',
    preview: 'Preview: {name}',
    missing: 'Missing attachments: {count}',
  },
  review: {
    fuel: {
      title: 'Fuel entries',
      description: 'Fuel review queue: check the receipt and warnings, then approve, reject or ask the driver for more information.',
      search: 'Search by station, plate, driver…',
      chipTotal: 'Fuel entries',
      chipWarned: 'With warnings',
      empty: 'No fuel entries to show.',
      emptyFiltered: 'No fuel entry matches the filters.',
      onlyWarned: 'Only with warnings',
      filterType: 'Fuel type',
      allTypes: 'All fuels',
      filterSource: 'Source',
      allSources: 'All sources',
      col: { date: 'Date', vehicle: 'Vehicle', driver: 'Driver', fuel: 'Fuel', liters: 'Litres', amount: 'Amount', odometer: 'Odometer', warnings: 'Warnings', receipt: 'Receipt', status: 'Status' },
      fields: {
        filledAt: 'Fill date',
        fuelType: 'Fuel type',
        liters: 'Litres',
        unitPrice: 'Price per litre',
        total: 'Total',
        odometer: 'Odometer',
        station: 'Station',
        payment: 'Payment',
        fuelCard: 'Fuel card',
        fullTank: 'Full tank',
        source: 'Source',
        comment: 'Driver notes',
      },
      fullTankYes: 'yes',
      fullTankNo: 'no',
      warningsTitle: 'Warnings to check',
      warningsHint: 'Warnings never block saving — they help catch mistakes and misuse before approval.',
      noWarnings: 'No warnings.',
      receiptTitle: 'Receipt photo',
      receiptMissing: 'No receipt photo.',
      receiptRequired: 'Approval requires a receipt photo (fleet setting).',
      correct: 'Correct data',
      correctTitle: 'Correct fuel entry',
      correctDescription: 'Fix the data after checking the receipt. The change is audited and warnings are recalculated.',
      correctSubmit: 'Save corrections',
      corrected: 'Fuel entry corrected.',
      errLiters: 'Litres: a number from 0.01 to 2000.',
      errAmount: 'Total: a number from 0 to 1,000,000.',
      errOdometer: 'Odometer: a whole number, 0 or more.',
      odometerLocked: 'The odometer cannot be changed because a reading was already created from it.',
    },
    expenses: {
      title: 'Vehicle expenses',
      description: 'Review queue for small expenses reported by drivers: parking, tolls, washing, fluids.',
      search: 'Search by description, plate, driver…',
      chipTotal: 'Expenses',
      empty: 'No expenses to show.',
      emptyFiltered: 'No expense matches the filters.',
      filterCategory: 'Category',
      allCategories: 'All categories',
      col: { date: 'Date', vehicle: 'Vehicle', driver: 'Driver', category: 'Category', amount: 'Amount', description: 'Description', documents: 'Documents', status: 'Status' },
      fields: { incurredAt: 'Date incurred', category: 'Category', amount: 'Amount', description: 'Description' },
      documentsTitle: 'Documents and photos',
    },
    selfReviewHint: 'This is your own entry — only someone else can review it.',
    noActions: 'This entry is closed — no actions available.',
    awaitingAuthor: 'Waiting for the driver to complete it.',
    dialog: {
      approve: 'Approve this entry? The comment is optional.',
      reject: 'Rejection is final — the driver gets a notification with your comment.',
      'request-info': 'The driver gets a notification and can complete the entry. Describe what is missing.',
    },
    commentRequired: 'A comment is required for this action.',
    done: { approve: 'Entry approved.', reject: 'Entry rejected.', 'request-info': 'The driver was asked for more information.' },
  },
  ...fleetReportsEnOps,
};
